import { useToolOutput } from "../../hooks/useToolOutput";
import { type ChatOutputFile, chatOutputFile } from "../../tools/ShowOutput";
import { BrowserChrome } from "../BrowserChrome";
import { jsonRecord, jsonString } from "../Chat/CommandRun";
import type { ChatProps } from "../Chat/chatTypes";
import { EmptyState } from "../EmptyState";
import { WavyLine } from "../WavyLine";
import { ChatpadPreview } from "./ChatpadPreview";

export type PageBuild = {
  entryId: string;
  file: ChatOutputFile;
};

export function pageBuildFile(entry: ChatProps["entries"][number]) {
  if (entry.toolCall?.toolName !== "execute") return null;
  const path = jsonString(jsonRecord(entry.toolCall.input)?.previewFile);
  return path && /\.html?$/i.test(path)
    ? chatOutputFile("showStorageFile", { path })
    : null;
}

export function BuildingPagePreview({
  build,
  entries,
  responding,
  awaitingAction,
  onBack,
  backLabel,
}: {
  build: PageBuild;
  entries: ChatProps["entries"];
  responding: boolean;
  awaitingAction: boolean;
  onBack: () => void;
  backLabel: string;
}) {
  const entry = entries.find((entry) => entry.id === build.entryId);
  const preview = entries.findLast(
    (candidate) =>
      candidate.createdAt >= (entry?.createdAt ?? 0) &&
      candidate.toolCall &&
      chatOutputFile(candidate.toolCall.toolName, candidate.toolCall.input)
        ?.key === build.file.key
  );
  const { output } = useToolOutput(
    build.entryId,
    entry?.toolCall?.completedAt != null
  );
  const failed =
    entry?.toolCall?.isError ||
    preview?.toolCall?.isError ||
    (output != null &&
      typeof output === "object" &&
      "status" in output &&
      output.status === "error");
  const title = failed
    ? preview?.toolCall?.isError
      ? "Couldn’t load page"
      : "Couldn’t build page"
    : preview
      ? "Loading page…"
      : awaitingAction
        ? "Waiting for your input"
        : responding
          ? "Building page…"
          : "Page not ready";
  const active = !failed && (responding || !!preview);
  return (
    <ChatpadPreview
      title={build.file.fileName}
      onBack={onBack}
      backLabel={backLabel}
    >
      <BrowserChrome displayUrl={build.file.fileName} embedded>
        <EmptyState
          icon={active ? <WavyLine /> : undefined}
          title={title}
          maxWidth={220}
          description={
            !active && !awaitingAction
              ? "Continue in chat to finish this page."
              : undefined
          }
        />
      </BrowserChrome>
    </ChatpadPreview>
  );
}
